/**
 * Crumble — the dissolving block (Brief 014). SHARED across SMB / Super Meat Boy /
 * Celeste: stand on it and it crumbles out from under you.
 *
 * A SOLID, tile-aligned 1×1 entity with a 3-state integer-step machine:
 *   solid → (stood on) crumbling [fuse counts down, still solid + shaking]
 *         → gone [non-solid + invisible]
 *         → (after respawnSteps, if > 0) solid again.
 *
 * Determinism: integer STEP counters only (one tick per update; the fuse/respawn never
 * read wall-clock) + min/max arithmetic → ESLint-clean under src/engine/. A respawn
 * rebuilds the entity fresh, so the state resets with the life.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const DEFAULT_FUSE = 36;     // ~0.6s before it drops once stood on
const DEFAULT_RESPAWN = 90;  // ~1.5s before it returns (0 = never)

export class Crumble implements Entity {
  readonly type = EntityType.CRUMBLE;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private fuseSteps: number;
  private respawnSteps: number;
  private state: 'solid' | 'crumbling' | 'gone' = 'solid';
  private timer = 0;       // integer step countdown for the active state
  private shakeTick = 0;   // render-only shake phase while crumbling

  /** @param props `fuseSteps` (default 36), `respawnSteps` (default 90; 0 = never returns). */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    this.body = { x: cx - ts / 2, y: feetY - ts, w: ts, h: ts };
    const f = props?.fuseSteps;
    this.fuseSteps = typeof f === 'number' && f >= 0 ? Math.floor(f) : DEFAULT_FUSE;
    const r = props?.respawnSteps;
    this.respawnSteps = typeof r === 'number' && r >= 0 ? Math.floor(r) : DEFAULT_RESPAWN;
  }

  /** Solid while solid OR crumbling (you can still stand on the fuse); null once gone. */
  solidBody(): AABB | null { return this.state === 'gone' ? null : this.body; }

  update(_dt: number, _ctx: EntityContext): void {
    if (this.state === 'crumbling') {
      this.shakeTick++;
      this.timer--;
      if (this.timer <= 0) { this.state = 'gone'; this.timer = this.respawnSteps; }
    } else if (this.state === 'gone' && this.respawnSteps > 0) {
      this.timer--;
      if (this.timer <= 0) { this.state = 'solid'; this.shakeTick = 0; }
    }
  }

  /** Any top-landing arms the fuse once (subsequent stands while crumbling are no-ops). */
  onStand(ctx: EntityContext, _landingVy: number): void {
    if (this.state !== 'solid') return;
    this.state = 'crumbling';
    this.timer = this.fuseSteps;
    ctx.playSound('break', 0.35); // POLISH_01 #1 — crumble-away shatter
  }

  onPlayerOverlap(_ctx: EntityContext): void { /* solid — acts via onStand */ }

  spriteKey(): string { return 'crumble'; }

  /** Invisible only while gone; shows (and shakes) through the fuse. */
  visible(): boolean { return this.state !== 'gone'; }

  renderYOffset(): number {
    if (this.state !== 'crumbling') return 0;
    return this.shakeTick % 2 === 0 ? -2 : 2; // 1-frame jitter
  }
}
